import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { usePrevious } from 'hooks';
import UserDevice from 'services/user-device';
import { parse } from 'utils';
import { Action, AnalyticsAttr, Category } from './constants';
import type { FieldsObj } from './types';
import { formatPathname, formatSearch, sendAnalytics } from './utils';

const GoogleAnalytics: React.FC = () => {
    const location = useLocation();
    const prevLocation = usePrevious(location);

    const { search, pathname, hash } = location;

    const prevUrl = useRef('Enter the site');

    const handleClickEvent: EventListener = useCallback((event: Event) => {
        const { target } = event;

        if (!target) return;

        // NOTE: additional check on closest method
        const dataset = (target as Element)?.closest<HTMLElement>(
            `[${AnalyticsAttr.category}]` as const
        )?.dataset;

        if (!dataset) return;

        const { category, action, label } = dataset;

        if (!category || !action) return;

        const fieldsObj: FieldsObj = {
            eventCategory: category,
            eventAction: action,
        };
        sendAnalytics(fieldsObj, label);
    }, []);

    useEffect(() => {
        const eventType = UserDevice.getInstance()?.isDeviceMobile()
            ? 'click'
            : 'mousedown';

        document.body.addEventListener(eventType, handleClickEvent);

        return () => {
            document.body.removeEventListener(eventType, handleClickEvent);
        };
    }, [handleClickEvent]);

    useEffect(() => {
        const url = `${formatPathname(pathname)}${formatSearch(search)}${hash}`;
        const label = `From: ${prevUrl.current}; To: ${url}`;
        const fieldsObject = {
            eventCategory: Category.AppCategory,
            eventAction: Action.LocationChange,
        };

        prevUrl.current = url;

        sendAnalytics(fieldsObject, label);
    }, [search, hash, pathname]);

    useEffect(() => {
        if (!prevLocation) return;

        const { pathname: prevPathName, search: prevSearch } = prevLocation;

        const currentPage = parse(search).page;

        const prevPage = parse(prevSearch).page;

        if ((!currentPage && !prevPage) || prevPathName !== pathname) return;

        const label = `From: ${prevPage ?? 1}; To: ${currentPage ?? 1}`;

        const fieldsObject = {
            eventCategory: Category.AppCategory,
            eventAction: Action.ChangePage,
        };
        sendAnalytics(fieldsObject, label);
    }, [pathname, prevLocation, search]);

    return null;
};

export default memo(GoogleAnalytics);
